import { filterResourcesByBuilderId } from '@ctw/shared/api/common';
import { usePatientDocuments } from '@ctw/shared/api/fhir/document';
import { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import {
  SYSTEM_SUMMARY,
  SYSTEM_ZUS_OWNER,
  SYSTEM_ZUS_SUMMARY,
  SYSTEM_ZUS_THIRD_PARTY,
} from '@ctw/shared/api/fhir/system-urls';
import { fqsRequestAll, MAX_OBJECTS_PER_REQUEST } from '@ctw/shared/api/fqs/client';
import {
  type EncounterGraphqlResponse,
  encountersQuery,
} from '@ctw/shared/api/fqs/queries/encounters';
import { getLensBuilderId } from '@ctw/shared/api/urls';
import { dedupeAndMergeEncounters } from '@ctw/shared/content/encounters/helpers/filters';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useEffect, useState } from 'react';
import { getLensSummariesWithSyncedWithRecord } from '../api/fhir/summary';
import { usePatientQuery } from '../context/patient-provider';
import {
  QUERY_KEY_PATIENT_BUILDER_ENCOUNTERS,
  QUERY_KEY_PATIENT_SUMMARY_ENCOUNTERS,
} from '../utils/query-keys';

export function usePatientEncounters(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  const queryForSummary = usePatientQuery({
    queryId: QUERY_KEY_PATIENT_SUMMARY_ENCOUNTERS,
    queryKey: [limit],
    queryFn: async ({ requestContext, telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
        telemetry,
        graphqlClient,
        encountersQuery,
        'EncounterConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: limit,
          filter: {
            tag: {
              allmatch: [
                `${SYSTEM_ZUS_SUMMARY}|Common`,
                `${SYSTEM_ZUS_OWNER}|builder/${getLensBuilderId(requestContext.env)}`,
              ],
            },
          },
        },
      );
      const nodes = data.EncounterConnection.edges.map((x) => x.node);
      const results = nodes.map(
        (c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList),
      );
      return dedupeAndMergeEncounters(results);
    },
    enabled,
  });
  const queryForBuilder = usePatientQuery({
    queryId: QUERY_KEY_PATIENT_BUILDER_ENCOUNTERS,
    queryKey: [limit],
    queryFn: async ({ requestContext, telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
        telemetry,
        graphqlClient,
        encountersQuery,
        'EncounterConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: limit,
          filter: {
            tag: {
              nonematch: [SYSTEM_SUMMARY, SYSTEM_ZUS_THIRD_PARTY],
              // TODO: There's a bug in FQS that doesn't allow filtering with nonematch AND allmatch.
              // Uncomment the line below once https://zeushealth.atlassian.net/browse/DRT-249 is resolved.
              // allmatch: [`${SYSTEM_ZUS_OWNER}|builder/${requestContext.builderId}`],
            },
          },
        },
      );
      let nodes = data.EncounterConnection.edges.map((x) => x.node);
      nodes = filterResourcesByBuilderId(nodes, requestContext.builderId);
      const results = nodes.map(
        (c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList),
      );
      return dedupeAndMergeEncounters(results);
    },
    enabled,
  });
  const [encounters, setEncounters] = useState<Array<EncounterModel>>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useEffect(() => {
    setEncounters(
      getLensSummariesWithSyncedWithRecord(queryForSummary.data ?? [], queryForBuilder.data ?? []),
    );
  }, [
    queryForSummary.data,
    queryForBuilder.data,
    queryForSummary.isLoading,
    queryForBuilder.isLoading,
  ]);

  return {
    ...queryForSummary,
    isLoading: queryForSummary.isLoading || queryForBuilder.isLoading,
    data: encounters,
  };
}

// Gets patient encounters along with clinical notes from any documents associated with each encounter.
export function usePatientEncountersWithClinicalNotes(limit = MAX_OBJECTS_PER_REQUEST) {
  const useRelatedBinaries = useFeatureFlag('ctw-encounter-notes-use-related-binaries');
  const encounterQuery = usePatientEncounters(limit);
  const documentsQuery = usePatientDocuments(MAX_OBJECTS_PER_REQUEST, !useRelatedBinaries);

  // Instead of returning `encounterQuery.data` directly from this hook, store the encounters in a
  // state variable we can use to force a re-render when the documents are loaded.
  const [encounters, setEncounters] = useState<Array<EncounterModel>>([]);

  // Run when encounters or documents change. When the flag is on, skip the document pool
  // entirely — notes indicator is driven off encounter.hasRelatedBinaries; drawer resolves
  // DocRefs on open. When the flag is off, encounters are returned immediately (with
  // isLoadingClinicalNotes=true); when documents finish loading, clinical notes are applied
  // and a re-render is forced via a new array reference.
  useEffect(() => {
    // Wait for encounters to load before doing anything.
    if (encounterQuery.isLoading) {
      return;
    }

    // Flag-on path: skip the document pool entirely.
    if (useRelatedBinaries) {
      setEncounters(encounterQuery.data);
      return;
    }

    if (encounterQuery.data.length === 0) {
      setEncounters([]);
      return;
    }

    const documents = documentsQuery.data ?? [];
    const areDocumentsLoaded = !documentsQuery.isLoading;

    for (const encounter of encounterQuery.data) {
      if (areDocumentsLoaded) {
        encounter.setClinicalNotesFromDocumentPool(documents);
      }
      encounter.isLoadingClinicalNotes = !areDocumentsLoaded;
    }

    setEncounters([...encounterQuery.data]);
  }, [
    encounterQuery.isLoading,
    encounterQuery.data,
    documentsQuery.isLoading,
    documentsQuery.data,
    useRelatedBinaries,
  ]);

  // Exclude documentsQuery state so hook returns before documents are loaded
  return {
    isLoading: encounterQuery.isLoading,
    isError: encounterQuery.isError,
    data: encounters,
  };
}
